import React from "react";
import Head from "../Components/Layout/Head";
import FooterNew from "../Components/Layout/FooterNew";
import {
    Mail,
    BrainCircuit,
    Bot,
    Workflow,
    TrendingUp,
    Sparkles,
    CheckCircle2,
    ArrowRight,
} from "lucide-react";

const RegisterWebinarGoogleForm = () => {

    const learnItems = [
        {
            icon: <BrainCircuit size={30} />,
            title: "AI Strategy For Businesses",
            desc: "Learn how businesses are identifying areas where AI can improve operations, productivity, and customer experience.",
        },
        {
            icon: <Workflow size={30} />,
            title: "Workflow Automation",
            desc: "Discover how repetitive business tasks and manual workflows can be automated using AI systems.",
        },
        {
            icon: <Bot size={30} />,
            title: "AI Agents & Tools",
            desc: "Explore AI agents, chatbots, and business AI tools that companies are implementing today.",
        },
        {
            icon: <TrendingUp size={30} />,
            title: "Growth & Scalability",
            desc: "See how AI can help businesses save time, reduce costs, and scale operations more efficiently.",
        },
    ];

    const benefits = [
        "Live AI business examples",
        "Real automation ideas",
        "AI opportunities for companies",
        "Beginner friendly webinar",
    ];

    return (
        <>
            <Head />

            <div className="bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 overflow-hidden relative">

                {/* BACKGROUND BLURS */}
                <div className="absolute top-0 left-0 w-72 h-72 bg-blue-300/30 rounded-full blur-3xl"></div>
                <div className="absolute top-96 right-0 w-72 h-72 bg-indigo-300/30 rounded-full blur-3xl"></div>

                {/* HERO SECTION */}
                <section className="relative overflow-hidden">

                    {/* BACKGROUND */}
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-indigo-100"></div>

                    {/* GLOW EFFECTS */}
                    <div className="absolute top-[-120px] left-[-120px] w-[320px] h-[320px] bg-blue-400/20 rounded-full blur-3xl"></div>

                    <div className="absolute bottom-[-120px] right-[-120px] w-[320px] h-[320px] bg-indigo-400/20 rounded-full blur-3xl"></div>

                    <div className="relative max-w-7xl mx-auto px-0 sm:px-6 lg:px-8 py-4 sm:py-8 lg:py-8">

                        <div className="relative overflow-hidden rounded-[2.5rem] border border-white/40 bg-white/70 backdrop-blur-2xl shadow-[0_25px_100px_rgba(0,0,0,0.08)]">

                            {/* TOP GRADIENT */}
                            <div className="h-2 w-full bg-gradient-to-r from-blue-500 via-indigo-500 to-cyan-400"></div>

                            <div className="px-5 sm:px-8 md:px-12 lg:px-20 py-14 sm:py-16 lg:py-12">

                                {/* BADGE */}
                                <div className="flex justify-center">

                                    <div className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-100 px-5 py-2 text-sm font-semibold text-blue-700 shadow-sm">

                                        <Sparkles size={16} />

                                        Free AI Business Webinar

                                    </div>

                                </div>

                                {/* HEADING */}
                                <div className="mt-8 text-center">

                                    <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black leading-[1.05] tracking-tight text-gray-900">

                                        Learn How AI Can
                                        <span className="block mt-3 bg-gradient-to-r from-blue-600 via-indigo-600 to-cyan-500 bg-clip-text text-transparent">
                                            Transform Your Business
                                        </span>

                                    </h1>

                                    <p className="mt-8 max-w-4xl mx-auto text-base sm:text-lg lg:text-xl leading-relaxed text-gray-600">

                                        Discover how businesses are using AI to automate workflows,
                                        improve customer experiences, reduce operational costs,
                                        and unlock new growth opportunities.

                                    </p>

                                </div>

                                {/* BENEFITS */}
                                <div className="mt-10 flex flex-wrap justify-center gap-3 sm:gap-4">

                                    {benefits.map((item, index) => (
                                        <div
                                            key={index}
                                            className="flex items-center gap-2 rounded-full border border-gray-200 bg-white px-4 py-2 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-md"
                                        >

                                            <CheckCircle2
                                                size={18}
                                                className="text-green-600 flex-shrink-0"
                                            />

                                            <span className="text-sm font-medium text-gray-700">
                                                {item}
                                            </span>

                                        </div>
                                    ))}

                                </div>

                                {/* CTA SECTION */}
                                <div className="mt-14 max-w-5xl mx-auto">

                                    <div className="relative overflow-hidden rounded-[2rem] bg-gradient-to-r from-blue-600 via-indigo-600 to-blue-700 p-[1px] shadow-2xl">

                                        <div className="rounded-[2rem] bg-gradient-to-r from-blue-600 via-indigo-600 to-blue-700 px-6 sm:px-8 lg:px-10 py-7 sm:py-8 lg:py-10 text-white">

                                            <div className="flex flex-start items-center gap-6 lg:gap-10">

                                                

                                                    <div className="flex-shrink-0 rounded-2xl bg-white/20 p-4 backdrop-blur-sm">

                                                        <Mail size={30} />

                                                    </div>

                                                    <div>

                                                        <h3 className="text-2xl sm:text-3xl font-bold leading-tight">
                                                            Register Now
                                                        </h3>

                                                        <p className="mt-3 max-w-2xl text-sm sm:text-base lg:text-lg leading-relaxed text-blue-100">

                                                            Webinar joining details, reminders,
                                                            and access links will be shared
                                                            with you via email after registration.

                                                        </p>

                                                    </div>

                                            </div>

                                        </div>

                                    </div>

                                </div>

                            </div>

                        </div>

                    </div>

                </section>

                {/* GOOGLE FORM SECTION */}
                <section className="relative pb-14 sm:pb-16 lg:pb-24">

                    <div className="max-w-6xl mx-auto px-0 sm:px-6 lg:px-8">

                        <div className="bg-white rounded-[2rem] shadow-[0_20px_80px_rgba(0,0,0,0.08)] overflow-hidden border border-gray-100">

                            {/* FORM HEADER */}
                            <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-blue-700 px-6 sm:px-8 py-8 sm:py-10 text-center">

                                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white">
                                    Webinar Registration Form
                                </h2>

                                <p className="text-blue-100 mt-3 text-sm sm:text-base lg:text-lg max-w-2xl mx-auto leading-relaxed">
                                    Fill out the form below to reserve your seat
                                    and learn how AI can create real business impact.
                                </p>

                            </div>

                            {/* FORM CONTAINER */}
                            <div className="bg-gray-50 p-2 sm:p-4 md:p-6 lg:p-8">

                                <div className="bg-white rounded-3xl overflow-hidden border border-gray-200 shadow-sm">

                                    <iframe
                                        src="https://docs.google.com/forms/d/e/1FAIpQLSfJx-5YHBhuEo_9y4aRXrN5QIgEtPn1AEHgBUZYR4X4xmFX-A/viewform?embedded=true"
                                        width="100%"
                                        height={window.innerWidth < 640 ? "1700" : window.innerWidth < 1024 ? "1450" : "1250"}
                                        frameBorder="0"
                                        marginHeight="0"
                                        marginWidth="0"
                                        title="Webinar Registration Form"
                                        className="w-full"
                                    >
                                        Loading…
                                    </iframe>

                                </div>

                            </div>

                        </div>

                    </div>

                </section>

                {/* WHAT YOU'LL LEARN */}
                <section className="relative pb-20 sm:pb-24">

                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

                        {/* SECTION HEADER */}
                        <div className="text-center mb-14 sm:mb-16">

                            <div className="inline-flex items-center gap-2 bg-indigo-100 text-indigo-700 px-4 py-2 rounded-full text-sm font-semibold border border-indigo-200">
                                Webinar Topics
                            </div>

                            <h2 className="mt-6 text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight">
                                What You'll Learn In This Webinar
                            </h2>

                            <p className="mt-5 text-gray-600 text-base sm:text-lg max-w-4xl mx-auto leading-relaxed">

                                We approach businesses and pitch practical AI ideas
                                tailored to their operations — helping them automate processes,
                                improve efficiency, reduce costs, and enhance customer experiences.

                            </p>

                        </div>

                        {/* CARDS */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6 lg:gap-8">

                            {learnItems.map((item, index) => (
                                <div
                                    key={index}
                                    className="group relative bg-white rounded-[2rem] p-7 lg:p-8 border border-gray-100 shadow-md hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 overflow-hidden"
                                >

                                    {/* HOVER GRADIENT */}
                                    <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-indigo-50 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                                    <div className="relative">

                                        {/* ICON */}
                                        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-100 to-indigo-100 text-blue-600 flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-sm">

                                            {item.icon}

                                        </div>

                                        {/* TITLE */}
                                        <h3 className="mt-6 text-xl lg:text-2xl font-bold text-gray-900 leading-snug">
                                            {item.title}
                                        </h3>

                                        {/* DESC */}
                                        <p className="mt-4 text-gray-600 text-sm sm:text-base leading-relaxed">
                                            {item.desc}
                                        </p>

                                    </div>

                                </div>
                            ))}

                        </div>

                    </div>

                </section>

                <FooterNew />

            </div>
        </>
    );
};

export default RegisterWebinarGoogleForm;